import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

@customElement('cadence-teacher-detail-page')
export class CadenceTeacherDetailPage extends LitElement {
  @property() teacherId = '';
  @state() private teacher: any = null;
  @state() private classes: any[] = [];
  @state() private loading = true;
  @state() private inviting = false;
  @state() private inviteLink = '';
  @state() private inviteEmailed = false;

  static styles = css`
    :host { display: block; }
    .back { color: var(--color-primary); text-decoration: none; font-size: 0.88rem; }
    .header { display: flex; align-items: center; gap: 16px; margin: var(--space-3) 0 var(--space-4); }
    h1 { margin: 0; font-size: 1.5rem; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: var(--space-4); margin-bottom: var(--space-5); }
    .field .k { color: var(--color-text-secondary); font-size: 0.8rem; }
    .field .v { font-weight: 600; }
    .account { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
    .account .status { display: flex; align-items: center; gap: 8px; }
    .muted { color: var(--color-text-secondary); }
    .banner { margin-top: 12px; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-secondary); font-size: 0.88rem; word-break: break-all; }
    .banner .link { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
    .banner a { color: var(--color-primary); }
    .card-gap { margin-bottom: var(--space-5); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  updated(c: Map<string, unknown>) { if (c.has('teacherId')) this.load(); }

  private async load() {
    if (!this.teacherId) return;
    this.loading = true;
    this.inviteLink = '';
    try {
      const [t, classes] = await Promise.all([
        api.get<any>(`/teachers/${this.teacherId}`),
        api.get<any[]>(`/teachers/${this.teacherId}/classes`),
      ]);
      this.teacher = t; this.classes = classes;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private get canManage() { return store.hasPermission('settings.edit'); }

  private async sendInvite() {
    this.inviting = true;
    try {
      const res = await api.post<{ link: string; emailed: boolean }>(`/teachers/${this.teacherId}/invite`, {});
      this.inviteLink = res.link;
      this.inviteEmailed = res.emailed;
      showToast(t('teachers.invite.sent'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.inviting = false; }
  }

  private async copyInvite() {
    try { await navigator.clipboard.writeText(this.inviteLink); showToast(t('teachers.invite.copied'), 'success'); }
    catch { /* clipboard may be unavailable; the link is visible to copy manually */ }
  }

  render() {
    if (this.loading || !this.teacher) return html`<cadence-loading></cadence-loading>`;
    const teacher = this.teacher;
    return html`
      <a class="back" href="/admin/teachers" data-link>← ${t('teachers.detail.backToList')}</a>
      <div class="header">
        <cadence-avatar name=${`${teacher.first_name} ${teacher.last_name}`} size="56"></cadence-avatar>
        <h1>${teacher.first_name} ${teacher.last_name}</h1>
      </div>
      <div class="grid">
        <div class="field"><div class="k">${t('teachers.detail.email')}</div><div class="v">${teacher.email}</div></div>
        <div class="field"><div class="k">${t('teachers.detail.phone')}</div><div class="v">${teacher.phone || '—'}</div></div>
        <div class="field"><div class="k">${t('teachers.detail.payType')}</div><div class="v">${teacher.pay_type === 'monthly_salary' ? t('teachers.payType.monthlySalary') : t('teachers.payType.perClass')}</div></div>
        <div class="field"><div class="k">${t('teachers.detail.payRate')}</div><div class="v">${teacher.pay_rate != null ? `${teacher.pay_rate} ${teacher.pay_currency ?? ''}` : '—'}</div></div>
      </div>
      ${this.canManage ? html`
        <cadence-card heading=${t('teachers.invite.heading')} class="card-gap">
          <div class="account">
            <div class="status">
              ${teacher.has_login
                ? html`<cadence-badge color="var(--color-success)">${t('teachers.invite.canLogin')}</cadence-badge>
                       <span class="muted">${t('teachers.invite.canLoginHint')}</span>`
                : html`<cadence-badge color="var(--color-text-secondary)">${t('teachers.invite.noLogin')}</cadence-badge>
                       <span class="muted">${t('teachers.invite.noLoginHint')}</span>`}
            </div>
            <cadence-button variant=${teacher.has_login ? 'secondary' : 'primary'} ?disabled=${this.inviting} @click=${() => this.sendInvite()}>
              ${teacher.has_login ? t('teachers.invite.resend') : t('teachers.invite.send')}
            </cadence-button>
          </div>
          ${this.inviteLink ? html`
            <div class="banner">
              <div>${this.inviteEmailed ? t('teachers.invite.emailedNote') : t('teachers.invite.copyNote')}</div>
              <div class="link">
                <a href=${this.inviteLink}>${this.inviteLink}</a>
                <cadence-button variant="ghost" @click=${() => this.copyInvite()}>${t('teachers.invite.copy')}</cadence-button>
              </div>
            </div>` : ''}
        </cadence-card>` : ''}

      <cadence-card heading=${t('teachers.detail.assignedClasses')}>
        ${this.classes.length
          ? html`<cadence-table .columns=${[
              { key: 'name', label: t('teachers.detail.columns.class') }, { key: 'day_of_week', label: t('teachers.detail.columns.day') },
              { key: 'start_time', label: t('teachers.detail.columns.time') }, { key: 'enrolled_count', label: t('teachers.detail.columns.enrolled'), align: 'right' },
            ]} .rows=${this.classes}></cadence-table>`
          : html`<cadence-empty-state heading=${t('teachers.detail.noClasses')}></cadence-empty-state>`}
      </cadence-card>
    `;
  }
}
